/**
 * The groups of Admin → Scheduling's sub-menu, in menu order. Each is `?section=<key>` on
 * /admin/scheduling; General is the page without one. Reminders shows only on the in-memory
 * prototype (the shared schedule's are on Patient messages), Health card photos only while its
 * settings load; an address naming a group that is not shown is corrected to General.
 */
export const SCHEDULE_SETTINGS_SECTIONS = [
  "general",
  "booking",
  "hours",
  "visit-types",
  "physicians",
  "reminders",
  "photos",
] as const;

export type ScheduleSettingsSection = (typeof SCHEDULE_SETTINGS_SECTIONS)[number];

export const DEFAULT_SCHEDULE_SETTINGS_SECTION: ScheduleSettingsSection = "general";

export const SCHEDULE_SETTINGS_SECTION_LABELS: Readonly<Record<ScheduleSettingsSection, string>> = {
  general: "General",
  booking: "Online booking",
  hours: "Hours & closures",
  "visit-types": "Visit types",
  physicians: "Physicians",
  reminders: "Reminders",
  photos: "Health card photos",
};

export function isScheduleSettingsSection(value: unknown): value is ScheduleSettingsSection {
  return (
    typeof value === "string" && (SCHEDULE_SETTINGS_SECTIONS as readonly string[]).includes(value)
  );
}

/** The route's search: a known section, or none (General). */
export function validateScheduleSettingsSearch(search: Record<string, unknown>): {
  section?: ScheduleSettingsSection;
} {
  return isScheduleSettingsSection(search.section) ? { section: search.section } : {};
}

/** The search for a section: General keeps the plain address. */
export function scheduleSettingsSearch(section: ScheduleSettingsSection): {
  section?: ScheduleSettingsSection;
} {
  return section === DEFAULT_SCHEDULE_SETTINGS_SECTION ? {} : { section };
}

export function scheduleSettingsHref(section: ScheduleSettingsSection): string {
  return section === DEFAULT_SCHEDULE_SETTINGS_SECTION
    ? "/admin/scheduling"
    : `/admin/scheduling?section=${section}`;
}
